import type { Assessment } from '../../../capability-equipment/assessment/adapters/syn-assessment-adapter';
import type { PublicationView } from '../../../capability-equipment/assets/adapters/local-asset-workflow-port';
import type { EvaluationState, RequirementProjection } from './contract';

/** A separately reviewed SYN rule must bind the actual test, not a capability name. */
export interface ExactTestRule {
  id: string;
  requirementId: string;
  capabilityId: string;
  definitionVersion: number;
  definitionDigest: string;
  operator: string;
  value: number;
  unit: string;
  method: string;
  observations: string;
  limitations: string;
}
export interface MatchingSource {
  status: 'AVAILABLE' | 'UNAVAILABLE';
  reason: string;
  observedAt: string;
  country: string;
  agency: string;
  publications: readonly PublicationView[];
  assessments: readonly Assessment[];
  /** All current publications were read back with their exact Owner reference. */
  exactAssetsVerified: boolean;
  withdrawnRequirementIds?: readonly string[];
}
export interface MatchResult {
  mappingState: RequirementProjection['mappingState'];
  assessmentState: string;
  matchState: EvaluationState;
  reasonCode: string;
  exactReferences: {
    definition: RequirementProjection['capabilityDefinitionExactRef'];
    assets: readonly PublicationView[];
    assessmentIds: readonly string[];
  };
  limitations: readonly string[];
  dataProvenance: { owner: string; sourceStatus: string; observedAt: string; scope: string };
}

/** Pure read projection. No resource-coverage claim exists, so this consumer cannot prove GAP. */
export function matchRequirement(
  row: RequirementProjection,
  source: MatchingSource,
  rule?: ExactTestRule,
): MatchResult {
  const ref = row.capabilityDefinitionExactRef;
  const related = source.assessments.filter((a) => a.definition.capabilityId === ref.capabilityId);
  const exact = related.filter(
    (a) =>
      a.definition.definitionVersion === ref.expectedDefinitionVersion &&
      a.definition.definitionDigest === ref.expectedDefinitionDigest,
  );
  const result = (
    matchState: EvaluationState,
    assessmentState: string,
    reasonCode: string,
  ): MatchResult => ({
    mappingState: row.mappingState,
    assessmentState,
    matchState,
    reasonCode,
    exactReferences: {
      definition: ref,
      assets: source.publications,
      assessmentIds: related.map((a) => a.assessmentId),
    },
    limitations: [
      row.note,
      ...row.memberRowFieldCompleteness.unknownFields.map((f) => `${f}=UNKNOWN`),
      'SYN ONLY / NON-OPERATIONAL',
      '机构资源覆盖范围未确认；无候选不等于 GAP',
      ...related.map((a) => a.limitations),
    ],
    dataProvenance: {
      owner: 'Scenario → 05 → S4 Owner',
      sourceStatus: source.status,
      observedAt: source.observedAt,
      scope: `${source.country}/${source.agency}`,
    },
  });
  if (source.status !== 'AVAILABLE') return result('UNKNOWN', 'SOURCE_UNAVAILABLE', source.reason);
  if (!source.exactAssetsVerified)
    return result('UNKNOWN', 'OWNER_UNAVAILABLE', 'ASSET_EXACT_READ_UNVERIFIED');
  if (
    source.assessments.some(
      (a) => a.asset.country !== source.country || a.asset.agency !== source.agency,
    ) ||
    source.publications.some((p) => p.country !== source.country || p.agency !== source.agency)
  )
    return result('UNKNOWN', 'SCOPE_DENIED', 'SCOPE_DENIED');
  if (
    source.withdrawnRequirementIds?.includes(row.requirementId) ||
    row.sourceState.includes('WITHDRAWN') ||
    row.sourceState.includes('HISTORICAL')
  )
    return result('UNKNOWN', 'NOT_APPLICABLE', 'SOURCE_WITHDRAWN_OR_HISTORICAL');
  if (row.mappingState === 'UNMAPPED')
    return result('NOT_EVALUATED', 'NO_EXACT_BINDING', ref.reason);
  if (row.mappingState !== 'MAPPED' || ref.binding !== 'EXACT')
    return result('UNKNOWN', 'DEFINITION_UNKNOWN', ref.reason);
  if (
    !ref.capabilityId ||
    ref.expectedDefinitionVersion !== ref.resolvedDefinitionVersion ||
    ref.expectedDefinitionDigest !== ref.resolvedDefinitionDigest ||
    ref.resolvedDefinitionStatus !== 'PUBLISHED'
  )
    return result('UNKNOWN', 'DEFINITION_MISMATCH', 'DEFINITION_EXACT_PIN_MISMATCH');
  if (related.length && !exact.length)
    return result(
      'UNKNOWN',
      'DEFINITION_MISMATCH',
      'ASSESSMENT_DEFINITION_VERSION_OR_DIGEST_MISMATCH',
    );
  const current = exact.filter(
    (a) =>
      a.applicability.status === 'CURRENT' &&
      source.publications.some(
        (p) =>
          p.assetId === a.asset.assetId &&
          p.publishedVersion === a.asset.publishedVersion &&
          p.digest === a.asset.digest,
      ),
  );
  // An unreadable assessment must never disappear into the absence branch.
  const unreadable = exact.find((a) => !['CURRENT', 'STALE'].includes(a.applicability.status));
  if (unreadable)
    return result('UNKNOWN', unreadable.applicability.status, unreadable.applicability.reason);
  if (exact.length && !current.length)
    return result('UNKNOWN', 'STALE', 'ASSET_OR_ASSESSMENT_VERSION_STALE');
  if (current.some((a) => a.decision === null))
    return result('UNKNOWN', 'UNDECIDED', 'ASSESSMENT_PENDING_DECISION');
  if (
    row.memberRowFieldCompleteness.unknownCount !== 0 ||
    row.memberRowFieldCompleteness.unknownFields.length
  )
    return result(
      'UNKNOWN',
      current.length ? 'DECIDED' : 'NO_EXACT_ASSESSMENT',
      !related.length && source.assessments.length
        ? 'DIFFERENT_DEFINITION_ID_AND_UNKNOWN_CONDITIONS'
        : 'REQUIREMENT_CONDITIONS_UNKNOWN',
    );
  if (!current.length)
    return result('NOT_EVALUATED', 'NO_EXACT_ASSESSMENT', 'NO_ASSESSMENT_FOR_EXACT_BINDING');
  if (
    !rule ||
    rule.requirementId !== row.requirementId ||
    rule.capabilityId !== ref.capabilityId ||
    rule.definitionVersion !== ref.expectedDefinitionVersion ||
    rule.definitionDigest !== ref.expectedDefinitionDigest ||
    row.predicate.state !== 'SYN_TEST_ONLY' ||
    row.predicate.testRuleRef !== rule.id ||
    row.predicate.operator !== rule.operator ||
    row.predicate.value !== rule.value ||
    row.predicate.unit !== rule.unit
  )
    return result('UNKNOWN', 'DECIDED', 'EXPLICIT_TEST_CONDITIONS_NOT_VERIFIED');
  const applicable = current.filter(
    (a) =>
      a.method === rule.method &&
      a.observations === rule.observations &&
      a.limitations === rule.limitations &&
      a.evidence.length > 0 &&
      a.evidence.every(
        (e) =>
          e.synthetic &&
          e.owner === '05' &&
          e.country === source.country &&
          e.scope === source.agency,
      ),
  );
  if (!applicable.length) return result('UNKNOWN', 'DECIDED', 'TEST_OR_EVIDENCE_BINDING_MISMATCH');
  if (applicable.some((a) => a.decision?.outcome === 'SYN_ASSESSED_SUPPORTED'))
    return result('CANDIDATE', 'SUPPORTED', 'EXACT_SYN_TEST_SUPPORTED');
  return result('UNKNOWN', 'DECIDED', 'RESOURCE_COVERAGE_OR_EVIDENCE_INCOMPLETE');
}
